import { logError } from "~/common/logging";
import { ResponseSchema, PropertyType } from "./ResponseSchema";

// IE/FF/Chrome handle ISO date format in Date.parse inconsistently so we need to have our own implementation
const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d*))?Z?$/;

const tryParseDate = (date: string) => {
    const parts = isoDateRegex.exec(date);
    if (parts) {
        // handling 1/10 and 1/100 and trimming extra digits (may come from C# DateTime)
        const ms = +((parts[7] || "") + "000").substring(0, 3);
        return new Date(+parts[1], +parts[2] - 1, +parts[3], +parts[4], +parts[5], +parts[6], ms);
    }

    return null;
};

const setupDatesInObject = (obj: any) => {
    for (const key in obj) {
        if (!obj.hasOwnProperty(key)) {
            continue;
        }

        const value = obj[key];
        if (!value) {
            continue;
        }

        if (typeof value === "string") {
            const date = tryParseDate(value);
            if (date) {
                obj[key] = date;
            }
        } else if (typeof value === "object") {
            setupDatesInObject(value);
        }
    }
};

export const parseObjectWithDate = (obj: any) => {
    if (!obj) {
        return obj;
    }

    if (typeof obj === "string") {
        return tryParseDate(obj) || obj;
    }

    // not array or object (boolean or number in case of json)
    if (typeof obj !== "object") {
        return obj;
    }

    setupDatesInObject(obj);

    return obj;
};

export const parseJsonWithDate = (json: string) => {
    if (!json) {
        return null;
    }

    const result = JSON.parse(json);
    return parseObjectWithDate(result);
};

function processDateField(parentObject: any, key: string) {
    const objectValue = parentObject[key];
    if (objectValue != null) {
        const date = tryParseDate(objectValue);
        if (date === null) {
            logError(new Error(`Date field '${key}' contains invalid value '${objectValue}'`));
        }
        parentObject[key] = date;
    }
}

function processDateArrayField(parentObject: any, key: string) {
    const objectValue = parentObject[key];
    if (Array.isArray(objectValue)) {
        const resultArray: Date[] = [];
        for (const value of objectValue) {
            const date = tryParseDate(value);
            if (date === null) {
                logError(new Error(`DateArray field '${key}' contains invalid value '${value}'`));
                continue;
            }
            resultArray.push(date);
        }

        parentObject[key] = resultArray;
    }
}

const processCustomFields = (parentObject: any, keyOfCustomFields: string) => {
    if (!parentObject) {
        return;
    }

    const customFieldsObject = parentObject[keyOfCustomFields];

    for (const key in customFieldsObject) {
        const objectValue = customFieldsObject[key];

        if (/DateTime\d{1,}/i.test(key)) {
            const date = tryParseDate(objectValue);
            if (date === null && objectValue != null) {
                logError(new Error(`Date customfield '${key}' contains invalid value '${objectValue}'`));
            }
            customFieldsObject[key] = date;
        }
    }
};

const processSchema = (parentObject: any, schema: ResponseSchema) => {
    if (!parentObject) {
        return parentObject;
    }

    if (Array.isArray(schema)) {
        const nestedSchema = schema[0];
        for (const value of parentObject) {
            processSchema(value, nestedSchema);
        }
    } else {
        for (const key in schema) {
            const schemaValueType = schema[key];

            if (schemaValueType === PropertyType.Date) {
                processDateField(parentObject, key);
            } else if (schemaValueType === PropertyType.DateArray) {
                processDateArrayField(parentObject, key);
            } else if (schemaValueType === PropertyType.CustomFields) {
                processCustomFields(parentObject, key);
            } else if (typeof schemaValueType === "object") {
                processSchema(parentObject[key], schemaValueType);
            }
        }
    }

    return parentObject;
};

export const parseJsonWithSchema = (schema: ResponseSchema) => (json: string) => {
    if (!json) {
        return null;
    }

    const result = JSON.parse(json);
    return processSchema(result, schema);
};
